import React, { useContext } from 'react';
import { Box, Column, Optional, Row, Wrapper, useCarouselControl } from '@honda-canada/design-system-react';

import AccessoryCreditOffers from './AccessoryCreditOffers';
import Carousel from './Carousel';
import Colours from './Colours/Colours';
import Context from '../service/Context';
import Ctas from './Ctas';
import Features from './Features';
import Fees from './Fees';
import FilterBar from './FilterBar';
import Offers from './Offers';
import Payment from './Payment';
import Pricing from './Pricing';
import Highlights from './Highlights';
import themeStyles from './styles/Details.styles';
import { ModelExplorationContext } from '../../ModelExplorationContext';
import { useAppName, useLanguage } from '../../../utils/sitecoreContext';
import { useConfiguration } from '../service/utils';

const PaymentAndFeesWrapper = themeStyles.apply(Box, 'PaymentAndFeesWrapper');

const DesktopTrimDetails = ({ showLandscapeTab, showStudioTab, showThreeSixtyTab, ...props }) => {
  const {
    componentType,
    trim,
    modelCategory,
    featuresPath,
    hasError,
    isFetching,
    paymentDetailsProps,
    isSellingPriceProvince,
    showInformationalApr,
    selectedExteriorColor,
    accessoryCreditOffers,
    hasImages,
    missingAssetPlaceholderImage,
    category,
    setSelectedAssetType,
    selectedAssetType,
    selectedAssetCategory,
    carouselItems,
    setSelectedAssetCategory,
    showAssetTypeToggle,
    exteriorColorSwatches,
    interiorColorSwatches,
    handleExteriorSelection,
    handleInteriorSelection,
    setShowInteriorError,
    showInteriorError,
    handleAvailableSelection,
  } = props;
  const appName = useAppName();
  const language = useLanguage();
  const { variant, vehicleType, paymentOptions, shouldFetchPayment, usesModelName } = useContext(Context);
  const { selectedTransmission, setSelectedTransmission, bapUrlParams, isDark } =
    useContext(ModelExplorationContext) || {};

  const showSellingPrice = trim.isSellingPriceProvince;

  const behaviour = useCarouselControl(carouselItems.length);
  const { currentSlide, setCurrentSlide } = behaviour;

  const {
    hasInteriorColors,
    exteriorColorLabel,
    showFeesLabel,
    ctaButtons,
    showAccessoryCreditOffer,
    showModelHighlights,
    showRebateDisclaimerIndicator,
  } = useConfiguration(variant);

  return (
    <>
      <Optional when={hasInteriorColors}>
        <FilterBar
          assetCategory={selectedAssetCategory}
          assetType={selectedAssetType}
          isDark={isDark}
          onAssetCategoryChange={setSelectedAssetCategory}
          onAssetTypeChange={setSelectedAssetType}
          showAssetTypeToggle={showAssetTypeToggle}
          showLandscapeTab={showLandscapeTab}
          showStudioTab={showStudioTab}
          showThreeSixtyTab={showThreeSixtyTab}
        />
      </Optional>

      <Wrapper data-testid="cy-trim-details-wrapper" paddingTop={!hasInteriorColors && 'xxl'}>
        <Row pb="m">
          <Column width={[2 / 3]}>
            <Carousel
              hasImages={hasImages}
              missingAssetPlaceholderImage={missingAssetPlaceholderImage}
              isDark={isDark}
              trim={trim}
              category={category}
              modelCategory={modelCategory}
              vehicleType={vehicleType}
              assetType={selectedAssetType}
              currentSlide={currentSlide}
              setCurrentSlide={setCurrentSlide}
              behaviour={behaviour}
              items={carouselItems}
            />
            <Ctas
              bapUrlParams={bapUrlParams}
              category={category}
              componentType={componentType}
              ctaButtons={ctaButtons}
              isDark={isDark}
              trim={trim}
              vehicleType={vehicleType}
              modelCategory={modelCategory}
            />
          </Column>
          <Column width={[1 / 3]}>
            <Pricing
              hasError={hasError}
              isDark={isDark}
              isFetching={isFetching}
              pricing={trim?.pricing}
              selectedTransmission={selectedTransmission}
              setSelectedTransmission={setSelectedTransmission}
              transmissions={trim?.transmissions}
              trimName={usesModelName ? trim?.modelName || trim?.name : trim?.name}
              variant={variant}
              modelCode={trim?.modelCode}
              paymentDetailsProps={paymentDetailsProps}
              showRebateDisclaimerIndicator={showRebateDisclaimerIndicator}
            />
            <Optional when={shouldFetchPayment}>
              <PaymentAndFeesWrapper showFeesLabel={showFeesLabel}>
                <Optional when={!hasError}>
                  <Payment
                    appName={appName}
                    isDark={isDark}
                    isSellingPriceProvince={isSellingPriceProvince}
                    language={language}
                    paymentOptions={paymentOptions}
                    showInformationalApr={showInformationalApr}
                    trim={{ exteriorColorKey: selectedExteriorColor, ...trim }}
                    vehicleType={vehicleType}
                  />
                </Optional>
                <Optional when={showFeesLabel}>
                  <Fees
                    pricing={trim?.pricing}
                    showSellingPrice={showSellingPrice}
                    language={language}
                    isDark={isDark}
                    showInformationalApr={showInformationalApr}
                    trim={{ ...trim, exteriorColorKey: selectedExteriorColor }}
                    vehicleType={vehicleType}
                    appName={appName}
                    isSellingPriceProvince={isSellingPriceProvince}
                  />
                </Optional>
              </PaymentAndFeesWrapper>
            </Optional>
            <AccessoryCreditOffers
              showAccessoryCreditOffer={showAccessoryCreditOffer}
              accessoryCreditOffers={accessoryCreditOffers}
            />
            <Offers
              isDark={isDark}
              modelKey={trim.modelKey}
              modelYear={trim.modelYear}
              transmissionKey={trim.transmissionKey}
            />
            <Optional when={exteriorColorSwatches && interiorColorSwatches}>
              <Colours
                exteriorColorLabel={exteriorColorLabel}
                exteriorSwatches={exteriorColorSwatches}
                gtmBodyStyle={trim?.gtmBodyStyle}
                gtmModelName={trim?.gtmModelName}
                gtmTrimName={trim?.gtmTrimName}
                hasInteriorColors={hasInteriorColors}
                interiorSwatches={interiorColorSwatches}
                isDark={isDark}
                setSelectedExteriorColor={handleExteriorSelection}
                setSelectedInteriorColor={handleInteriorSelection}
                setShowInteriorError={setShowInteriorError}
                showInteriorError={showInteriorError}
                setCurrentSlide={setCurrentSlide}
              />
            </Optional>
            <Optional when={!hasInteriorColors}>
              <Colours
                exteriorColorLabel={exteriorColorLabel}
                exteriorSwatches={exteriorColorSwatches}
                gtmBodyStyle={trim?.gtmBodyStyle}
                gtmModelName={trim?.gtmModelName}
                gtmTrimName={trim?.gtmTrimName}
                hasInteriorColors={hasInteriorColors}
                isDark={isDark}
                setSelectedExteriorColor={handleAvailableSelection}
                setCurrentSlide={setCurrentSlide}
              />
            </Optional>
            <Optional when={showModelHighlights && trim?.modelHighlights}>
              <Highlights modelHighlights={trim?.modelHighlights} />
            </Optional>
          </Column>
        </Row>
      </Wrapper>
      <Optional when={featuresPath}>
        <Features path={featuresPath} />
      </Optional>
    </>
  );
};

export default DesktopTrimDetails;
